import { useMutation } from "@apollo/client";
import { ErrorBoundary } from "@sentry/react";
import { forwardRef } from "react";
import {
  AskAiChatMessageFragmentFragment,
  ChatSessionMessageReactionFragmentFragment,
  ChatSessionReactionType,
  ChatbotRoleEnum,
  CreateChatMessageReactionMutation,
  CreateChatMessageReactionMutationVariables,
} from "types/graphql-schema";

import { successNotificationVar } from "@cache/cache";
import Button, { buttonTheme } from "@components/button/button";
import ErrorFallback from "@components/error-fallback/error-fallback";
import { getTopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import { classNames } from "@helpers/css";
import { sanitizeMarkdown } from "@helpers/string";

import createChatMessageReactionMutation from "../graphql/create-chat-message-reaction-mutation";
import { parsePartialJson } from "../helpers";
import AskAiEmbeds from "./ask-ai-embeds";
import AskAIMessageMarkdown from "./ask-ai-message-markdown";
import AskAiPlaceholders from "./ask-ai-placeholders";

export const AskAIMessage = forwardRef(
  (
    {
      message,
      chatSessionId,
      reaction,
      isLastMessage,
      className,
      style,
    }: {
      message: AskAiChatMessageFragmentFragment;
      chatSessionId: number | null;
      reaction: ChatSessionMessageReactionFragmentFragment | null;
      isLastMessage: boolean;
      className?: string;
      style?: React.CSSProperties;
    },
    ref: React.Ref<HTMLDivElement>
  ) => {
    const { isStreaming, content } = message;
    const isStreamingJSON = isStreaming && content.trim().startsWith("{");
    const localStreamedContent = isStreamingJSON
      ? parsePartialJson(content)
      : null;

    const [createChatReaction] = useMutation<
      CreateChatMessageReactionMutation,
      CreateChatMessageReactionMutationVariables
    >(createChatMessageReactionMutation);

    const handleClickThumbsUp = () => {
      if (!chatSessionId) {
        return;
      }
      createChatReaction({
        variables: {
          chatSessionId,
          messageId: message.id,
          reactionType: ChatSessionReactionType.ThumbsUp,
        },
        onCompleted: () => {
          successNotificationVar({ title: "Thank you for your feedback!" });
        },
      });
    };
    const handleClickThumbsDown = () => {
      if (!chatSessionId) {
        return;
      }
      createChatReaction({
        variables: {
          chatSessionId,
          messageId: message.id,
          reactionType: ChatSessionReactionType.ThumbsDown,
        },
        onCompleted: () => {
          successNotificationVar({ title: "Thank you for your feedback!" });
        },
      });
    };

    const isThumbsUp =
      !!reaction && reaction.reactionType === ChatSessionReactionType.ThumbsUp;
    const isThumbsDown =
      !!reaction &&
      reaction.reactionType === ChatSessionReactionType.ThumbsDown;

    let contentJson = null;
    try {
      contentJson = JSON.parse(content);
    } catch (error) {}

    const markdownContent =
      (isStreamingJSON
        ? // sanitize the markdown to avoid unclosed opening brackets
          sanitizeMarkdown(localStreamedContent?.explanation ?? "")
        : isStreaming
        ? sanitizeMarkdown(content)
        : content) || "";

    return (
      <ErrorBoundary fallback={ErrorFallback}>
        <div
          className={classNames(
            !isLastMessage && "group/message-reaction",
            className
          )}
          style={style}
          ref={ref}
        >
          <div
            className={classNames(
              "w-full text-gray-700 flex",
              message.role === ChatbotRoleEnum.User
                ? "justify-end pl-12"
                : "flex-col gap-4"
            )}
            key={message.id}
            id={message.id}
          >
            {!contentJson && !!markdownContent && (
              <div
                className={classNames(
                  "prose prose-sm max-w-none",
                  "bg-white",
                  message.role === ChatbotRoleEnum.User &&
                    "rounded-3xl py-2 px-4 bg-gray-100",
                  message.error && "text-red-600"
                )}
              >
                <AskAIMessageMarkdown>{markdownContent}</AskAIMessageMarkdown>
              </div>
            )}
            <div className="empty:hidden">
              {/* {isLastMessage && ( // testing
                <div className="outline outline-red-500/20">
                  <AskAiPlaceholders
                    content={{
                      output_items: [
                        {
                          type: "goal_list",
                          explanation: "Here are the goals I found for you",
                          items: [{ type: "goal", id: 7 }],
                        },
                      ],
                    }}
                  />
                </div>
              )} */}
              {/* while it is streaming, we are trying to display as much informations as we can and as fast. We call those placeholders. */}
              {isStreamingJSON ? (
                <AskAiPlaceholders content={localStreamedContent} />
              ) : (
                // once streaming is complete, we render the final embeds
                <AskAiEmbeds
                  chatSessionId={chatSessionId}
                  message={message}
                  isLastMessage={isLastMessage}
                />
              )}
            </div>
          </div>
          {message.role === ChatbotRoleEnum.Assistant && (
            <div
              className={classNames(
                "flex gap-1 items-center mt-4",
                !isLastMessage &&
                  !reaction &&
                  "opacity-0 group-hover/message-reaction:opacity-100"
              )}
            >
              <Button
                icon={getTopicflowIcon("thumbsUp")}
                onClick={handleClickThumbsUp}
                theme={buttonTheme.iconGray}
                disabled={isThumbsUp || !!isStreaming}
                className={classNames(
                  isThumbsUp && "opacity-100 text-gray-900",
                  isThumbsDown && "opacity-50 hover:opacity-100"
                )}
                aria-label="Thumbs up"
              />
              <Button
                icon={getTopicflowIcon("thumbsDown")}
                onClick={handleClickThumbsDown}
                theme={buttonTheme.iconGray}
                disabled={isThumbsDown || !!isStreaming}
                className={classNames(
                  isThumbsDown && "opacity-100 text-gray-900",
                  isThumbsUp && "opacity-50 hover:opacity-100"
                )}
                aria-label="Thumbs down"
              />
            </div>
          )}
        </div>
      </ErrorBoundary>
    );
  }
);

export default AskAIMessage;
